import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Label,
  Select,
  Spinner,
  Textarea,
  TextInput,
} from "flowbite-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "../../config/axiosInstance";
import { CRM, CUSTOM_FIELDS, LEADS } from "../../config/apiConfig";
import CorporateBreadcrumb from "../common/CorporateBreadcrumb";
import AnimatedToast from "../common/AnimatedToast";
import {
  HiArrowDown,
  HiArrowLeft,
  HiArrowUp,
  HiClipboardDocumentCheck,
  HiChevronDoubleRight,
  HiLink,
  HiTrash,
  HiX,
  HiCheck,
} from "react-icons/hi";
import { HiMiniClipboardDocumentCheck } from "react-icons/hi2";

const STANDARD_LEAD_FIELDS = [
  { key: "name", label: "Nome completo", type: "Texto" },
  { key: "email", label: "E-mail principal", type: "E-mail" },
  { key: "phoneNumber", label: "Telefone", type: "Telefone" },
  { key: "companyName", label: "Empresa", type: "Texto" },
  { key: "notes", label: "Observações", type: "Texto longo" },
];

const SOURCE_LABELS = {
  lead: "Contato",
  leadCustomField: "Campo customizado do contato",
  cardCustomField: "Campo do card",
};

const FIELD_TYPE_LABELS = {
  text: "Texto",
  textarea: "Texto longo",
  number: "Número",
  currency: "Moeda",
  percent: "Porcentagem",
  email: "E-mail",
  tel: "Telefone",
  phone: "Telefone",
  boolean: "Booleano",
  date: "Data",
  datetime: "Data e hora",
  single_select: "Seleção única",
  select: "Seleção",
  multi_select: "Seleção múltipla",
  radio: "Botão de opção",
  checkbox: "Checkbox",
  textarea_long: "Texto longo",
};

const getFieldTypeLabel = (type) => {
  if (!type) return "Texto";
  const key = String(type).toLowerCase();
  return (
    FIELD_TYPE_LABELS[key] ||
    FIELD_TYPE_LABELS[key.replace(/\s+/g, "_")] ||
    "Texto"
  );
};

const buildFieldKey = (source, key) => `${source}:${key}`;
const MANDATORY_LEAD_FIELDS = ["name", "phoneNumber"];

export default function FunnelPublicFormBuilder() {
  const { funnelId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedFields, setSelectedFields] = useState([]);
  const [targetStageId, setTargetStageId] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [copyFeedback, setCopyFeedback] = useState("");
  const [error, setError] = useState("");
  const [hasSyncedSavedConfig, setHasSyncedSavedConfig] = useState(false);
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [dragFieldIndex, setDragFieldIndex] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);
  const [isDragOverFields, setIsDragOverFields] = useState(false);
  const [availableSearch, setAvailableSearch] = useState("");
  const [animatedToast, setAnimatedToast] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const {
    data: funnel,
    isLoading: loadingFunnel,
    error: funnelError,
  } = useQuery(
    ["crm-funnel-details", funnelId],
    async () => {
      const res = await axios.get(`${CRM.FUNNEL_DETAILS}/${funnelId}/details`);
      return res?.data?.data ?? null;
    },
    {
      enabled: !!funnelId,
    }
  );

  const { data: leadCustomFields = [], isLoading: loadingLeadFields } =
    useQuery(
      ["lead-custom-fields"],
      async () => {
        const res = await axios.get(LEADS.CUSTOM_FIELDS);
        const data = res?.data?.data || [];
        return Array.isArray(data) ? data : [];
      },
      {
        enabled: !!funnelId,
      }
    );

  const { data: cardCustomFields = [], isLoading: loadingCardFields } =
    useQuery(
      ["funnel-card-fields", funnelId],
      async () => {
        const res = await axios.get(CUSTOM_FIELDS.LIST(funnelId));
        const data = res?.data?.data || [];
        return Array.isArray(data) ? data : [];
      },
      {
        enabled: !!funnelId,
      }
    );

  const { data: savedConfig, isLoading: loadingConfig } = useQuery(
    ["funnel-public-form", funnelId],
    async () => {
      const res = await axios.get(CRM.FUNNEL_PUBLIC_FORM(funnelId));
      return res?.data?.data ?? null;
    },
    {
      enabled: !!funnelId,
    }
  );

  useEffect(() => {
    if (hasSyncedSavedConfig) return;
    if (targetStageId) return;
    if (!funnel?.stages?.length) return;
    const first = funnel.stages[0];
    if (first?.id) {
      setTargetStageId(String(first.id));
    }
  }, [funnel, targetStageId, hasSyncedSavedConfig]);

  const stageOptions = useMemo(() => {
    if (!funnel?.stages?.length) return [];
    return [...funnel.stages]
      .map((stage) => ({
        value: String(stage.id),
        label: stage.name || "Etapa",
      }))
      .filter((stage) => stage.value);
  }, [funnel?.stages]);

  const breadcrumbItems = useMemo(() => {
    const items = [];
    if (funnel?.name) {
      items.push({
        label: funnel.name,
        href: funnel?.id ? `/crm?f=${funnel.id}` : undefined,
      });
    } else {
      items.push({ label: "Funil" });
    }
    items.push({ label: "Formulário público" });
    return items;
  }, [funnel?.name, funnel?.id]);

  const isFieldSelected = (source, key) =>
    selectedFields.some(
      (field) => field.source === source && String(field.key) === String(key)
    );

  const addFieldFromSidebar = (payload) => {
    setError("");
    if (!payload || !payload.key) return;
    setSelectedFields((prev) => {
      const exists = prev.some(
        (field) =>
          field.source === payload.source &&
          String(field.key) === String(payload.key)
      );
      if (exists) return prev;
      return [
        ...prev,
        {
          ...payload,
          label: payload.label || payload.defaultLabel || "",
          required: !!payload.required,
          id: buildFieldKey(payload.source, payload.key),
        },
      ];
    });
  };

  const updateField = (idx, patch) => {
    setSelectedFields((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const removeField = (idx) => {
    setSelectedFields((prev) => {
      const target = prev[idx];
      if (target?.systemRequired) {
        setError("Campos mínimos obrigatórios não podem ser removidos.");
        return prev;
      }
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
  };

  const moveField = (from, to) => {
    setSelectedFields((prev) => {
      if (
        from === null ||
        to === null ||
        from === to ||
        from < 0 ||
        to < 0 ||
        from >= prev.length
      ) {
        return prev;
      }
      const next = [...prev];
      const [item] = next.splice(from, 1);
      const normalizedTo = Math.min(Math.max(0, to), next.length);
      next.splice(normalizedTo, 0, item);
      return next;
    });
  };

  const handleFieldDragStart = (idx) => (event) => {
    setDragFieldIndex(idx);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", idx);
  };

  const handleFieldDragOver = (idx) => (event) => {
    event.preventDefault();
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    const middleY = rect.top + rect.height / 2;
    const shouldInsertAfter = event.clientY > middleY;
    const nextIndex = shouldInsertAfter ? idx + 1 : idx;
    setDropTargetIndex(nextIndex);
    event.dataTransfer.dropEffect = "move";
  };

  const handleFieldDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const fromStr = event.dataTransfer.getData("text/plain");
    const fromIdx = fromStr ? Number(fromStr) : dragFieldIndex;
    const targetIndex =
      dropTargetIndex !== null && dropTargetIndex !== undefined
        ? dropTargetIndex
        : dragFieldIndex;
    if (
      fromIdx === undefined ||
      fromIdx === null ||
      targetIndex === undefined ||
      targetIndex === null
    ) {
      setDragFieldIndex(null);
      setDropTargetIndex(null);
      return;
    }
    moveField(fromIdx, targetIndex);
    setDragFieldIndex(null);
    setDropTargetIndex(null);
  };

  const handleFieldDragEnd = () => {
    setDragFieldIndex(null);
    setDropTargetIndex(null);
  };

  const handleAvailableFieldDragStart = (event, payload) => {
    if (!payload) return;
    event.dataTransfer.setData("text/plain", JSON.stringify(payload));
    event.dataTransfer.effectAllowed = "copy";
  };

  const handleSelectedFieldsDragOver = (event) => {
    event.preventDefault();
    if (!isDragOverFields) setIsDragOverFields(true);
  };

  const handleSelectedFieldsDrop = (event) => {
    event.preventDefault();
    setIsDragOverFields(false);
    const data = event.dataTransfer.getData("text/plain");
    if (!data) return;
    try {
      const parsed = JSON.parse(data);
      addFieldFromSidebar(parsed);
    } catch (err) {
      // ignore
    }
  };

  const handleSelectedFieldsDragLeave = (event) => {
    const relatedTarget =
      event.relatedTarget || event.nativeEvent?.relatedTarget;
    if (relatedTarget && event.currentTarget.contains(relatedTarget)) {
      return;
    }
    setIsDragOverFields(false);
  };

  const handleDropIndicatorDragOver = (index) => (event) => {
    event.preventDefault();
    setDropTargetIndex(index);
    event.dataTransfer.dropEffect = "move";
  };

  const normalizedLeadOptions = useMemo(
    () =>
      STANDARD_LEAD_FIELDS.map((item) => ({
        source: "lead",
        key: item.key,
        label: item.label,
        defaultLabel: item.label,
        type: item.type,
      })),
    []
  );

  const normalizedLeadCustomOptions = useMemo(
    () =>
      (leadCustomFields || []).map((field) => ({
        source: "leadCustomField",
        key: String(field.id),
        label: field.label,
        defaultLabel: field.label,
        required: !!field.isRequired,
        type: field.type,
      })),
    [leadCustomFields]
  );

  const requiredLeadCustomOptions = useMemo(
    () => normalizedLeadCustomOptions.filter((field) => field.required),
    [normalizedLeadCustomOptions]
  );

  const normalizedCardFieldOptions = useMemo(
    () =>
      (cardCustomFields || []).map((field) => ({
        source: "cardCustomField",
        key: String(field.id),
        label: field.label,
        defaultLabel: field.label,
        required: !!field.required,
        type: field.type,
      })),
    [cardCustomFields]
  );

  const baseMandatoryFields = useMemo(() => {
    const mandatoryLeadFields = normalizedLeadOptions
      .filter((option) => MANDATORY_LEAD_FIELDS.includes(option.key))
      .map((option) => ({
        ...option,
        required: true,
        systemRequired: true,
        id: buildFieldKey(option.source, option.key),
      }));

    const mandatoryCustomFields = requiredLeadCustomOptions.map((option) => ({
      ...option,
      required: true,
      systemRequired: true,
      id: buildFieldKey(option.source, option.key),
    }));

    return [...mandatoryLeadFields, ...mandatoryCustomFields];
  }, [normalizedLeadOptions, requiredLeadCustomOptions]);

  useEffect(() => {
    if (hasSyncedSavedConfig) return;
    if (!baseMandatoryFields.length) return;
    setSelectedFields((prev) => {
      const existingIds = new Set(prev.map((field) => field.id));
      const nextFields = [...prev];
      baseMandatoryFields.forEach((field) => {
        if (!existingIds.has(field.id)) {
          nextFields.push(field);
        }
      });
      return nextFields.length === prev.length ? prev : nextFields;
    });
  }, [baseMandatoryFields, hasSyncedSavedConfig]);

  useEffect(() => {
    if (typeof savedConfig === "undefined") return;
    if (!savedConfig) {
      setSelectedFields(baseMandatoryFields);
      setShareUrl("");
      setFormTitle("");
      setFormDescription("");
      setHasSyncedSavedConfig(false);
      return;
    }
    setTargetStageId(savedConfig.stageId ? String(savedConfig.stageId) : "");
    setShareUrl(savedConfig.publicUrl || "");
    setFormTitle(savedConfig.title || "");
    setFormDescription(savedConfig.description || "");
    const normalized = (savedConfig.fields || []).map((field) => ({
      source: field.source,
      key: field.key,
      label: field.label || "",
      required: !!field.required,
      type: field.type || "",
      id: buildFieldKey(field.source, field.key),
    }));
    const merged = [...normalized];
    const existingIds = new Set(normalized.map((field) => field.id));
    baseMandatoryFields.forEach((field) => {
      if (!existingIds.has(field.id)) {
        merged.push(field);
      }
    });
    setSelectedFields(merged);
    setHasSyncedSavedConfig(true);
  }, [savedConfig, baseMandatoryFields]);

  const copyLink = async () => {
    if (!shareUrl) return;
    if (!navigator?.clipboard?.writeText) {
      setCopyFeedback("Copie o link manualmente.");
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyFeedback("Link copiado");
      setTimeout(() => setCopyFeedback(""), 2000);
    } catch (err) {
      setCopyFeedback("Não foi possível copiar");
    }
  };

  const mutation = useMutation(
    async (payload) => {
      const res = await axios.put(CRM.FUNNEL_PUBLIC_FORM(funnelId), payload);
      return res?.data?.data ?? res?.data;
    },
    {
      onSuccess: (data) => {
        setShareUrl(data?.publicUrl || "");
        queryClient.invalidateQueries(["funnel-public-form", funnelId]);
        setAnimatedToast({
          id: Date.now(),
          title: "Sucesso",
          message: "Formulário público salvo com sucesso.",
        });
      },
      onError: (err) => {
        setError(
          err?.response?.data?.message ||
            "Não foi possível salvar o formulário público."
        );
      },
    }
  );

  const canSave =
    Boolean(targetStageId) &&
    selectedFields.length > 0 &&
    !mutation.isLoading &&
    !loadingConfig &&
    !loadingFunnel;

  const handleSave = () => {
    if (!canSave) return;
    setError("");
    const payload = {
      stageId: targetStageId,
      title: formTitle,
      description: formDescription,
      fields: selectedFields.map((field) => ({
        source: field.source,
        key: field.key,
        label: field.label,
        required: !!field.required,
        type: field.type,
      })),
    };
    mutation.mutate(payload);
  };

  const selectedFieldsPanelClass = [
    "space-y-3 rounded-2xl bg-white p-4 shadow-sm transition",
    isDragOverFields
      ? "border-2 border-orange-500 shadow-inner"
      : "border border-gray-200",
  ].join(" ");

  const normalizedSearch = availableSearch.trim().toLowerCase();
  const availableSections = useMemo(() => {
    const baseSections = [
      {
        title: "Campos do contato",
        items: normalizedLeadOptions,
        loading: false,
        emptyMessage: "Não há campos padrão.",
      },
      {
        title: "Campos customizados do contato",
        items: normalizedLeadCustomOptions,
        loading: loadingLeadFields,
        emptyMessage: "Não há campos customizados cadastrados ainda.",
      },
      {
        title: "Campos customizados do card",
        items: normalizedCardFieldOptions,
        loading: loadingCardFields,
        emptyMessage: "Cadastre campos customizados no funil antes.",
      },
    ];

    if (!normalizedSearch) return baseSections;

    return baseSections.map((section) => {
      const filteredItems = section.items.filter((item) => {
        const text = `${item.label || ""} ${item.type || ""}`.toLowerCase();
        return text.includes(normalizedSearch);
      });
      return { ...section, items: filteredItems };
    });
  }, [
    normalizedLeadOptions,
    normalizedLeadCustomOptions,
    normalizedCardFieldOptions,
    loadingLeadFields,
    loadingCardFields,
    normalizedSearch,
  ]);

  const renderAvailableFields = () => (
    <div className="rounded-2xl border border-gray-200 bg-gray-200 p-4 shadow-sm">
      <div className="flex flex-col mb-3">
        <p className="text-sm font-semibold">Campos disponíveis</p>
        <span className="text-xs">Arraste ou clique para adicionar</span>
      </div>
      <div className="mb-3">
        <TextInput
          size="sm"
          placeholder="Buscar campo..."
          value={availableSearch}
          onChange={(event) => setAvailableSearch(event.target.value)}
          className="rounded-2xl border-gray-200 shadow-sm"
        />
      </div>
      <div
        className="pr-2 overflow-y-auto bg-gray-100 rounded-lg p-2"
        style={{ maxHeight: "600px" }}
      >
        {availableSections.map((section) => (
          <section key={section.title} className="space-y-2 pt-3">
            <p className="text-xs font-semibold uppercase text-gray-500">
              {section.title}
            </p>
            {section.loading ? (
              <Spinner size="sm" />
            ) : !section.items.length ? (
              <p className="text-[11px] text-gray-500">
                {section.emptyMessage}
              </p>
            ) : (
              <div className="space-y-2 overflow-y-auto pr-1">
                {section.items.map((item) => {
                  const selected = isFieldSelected(item.source, item.key);
                  return (
                    <button
                      key={`${item.source}-${item.key}`}
                      type="button"
                      className={`w-full rounded-lg border px-3 py-2 text-left transition bg-white shadow-sm ${
                        selected
                          ? "border-orange-500 bg-orange-200"
                          : "border-gray-200 hover:border-orange-300 hover:bg-orange-100"
                      }`}
                      onClick={() => addFieldFromSidebar(item)}
                      draggable
                      onDragStart={(event) =>
                        handleAvailableFieldDragStart(event, item)
                      }
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium">
                            {item.label}
                          </span>
                          <p className="text-[11px] text-gray-500">
                            {item.type || "Texto"}
                          </p>
                        </div>
                        <span
                          className={`h-6 w-6 flex items-center justify-center rounded-full border ${
                            selected
                              ? "bg-orange-600 border-orange-600"
                              : "bg-white border-gray-300"
                          }`}
                        >
                          {selected && (
                            <HiCheck className="text-white" fontSize={14} />
                          )}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1">
                        {selected ? "Já adicionado" : "Adicionar"}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <CorporateBreadcrumb items={breadcrumbItems} />
      <header className="flex flex-col gap-2">
        <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
          Formulário público
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Crie e edite formulários públicos
            </h1>
          </div>
        </div>
      </header>

      {shareUrl && (
        <div className="relative overflow-hidden rounded-xl border border-orange-100 bg-gradient-to-br from-white to-orange-50 p-4 shadow-sm">
          <div className="absolute inset-0 opacity-40 blur-2xl" />
          <div className="relative flex gap-3">
            <div className="hidden md:flex h-10 w-10 items-center justify-center rounded-2xl bg-white/60 text-orange-500 shadow-inner">
              <HiLink className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="text-[11px] uppercase tracking-[0.4em] text-orange-500">
                Link público ativo
              </p>
              <p className="mt-1 break-all text-sm font-semibold text-slate-900">
                {shareUrl}
              </p>
            </div>
            <Button size="xs" color="gray" onClick={copyLink}>
              <div className="flex flex-col md:flex-row justify-center items-center md:gap-1">
                <HiMiniClipboardDocumentCheck className="h-4 w-4" />
                <span className="hidden md:block">Copiar</span>
              </div>
            </Button>
          </div>
          {copyFeedback && (
            <p className="mt-3 text-[11px] text-slate-500">{copyFeedback}</p>
          )}
        </div>
      )}

      {(mutation.isError || error || funnelError) && (
        <Alert color="failure">
          {error || funnelError?.message || "Erro ao carregar dados do funil."}
        </Alert>
      )}

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex-1 space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-6 shadow-sm">
            <div>
              <Label htmlFor="target-stage" className="font-semibold">
                Etapa de destino
              </Label>
              {loadingFunnel ? (
                <Spinner size="sm" />
              ) : (
                <Select
                  id="target-stage"
                  value={targetStageId}
                  onChange={(e) => setTargetStageId(e.target.value)}
                >
                  <option value="">Selecione uma etapa...</option>
                  {stageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Essa etapa vai receber os contatos criados via formulário
                público.
              </p>
            </div>

            <div className="space-y-4 border-t border-dashed border-gray-200 pt-4">
              <div className="space-y-2">
                <Label value="Título do formulário" className="font-semibold" />
                <TextInput
                  placeholder="Ex: Quero ser atendido"
                  value={formTitle}
                  onChange={(event) => setFormTitle(event.target.value)}
                />
                <p className="text-[11px] text-gray-500">
                  Esse título aparece no topo do formulário público.
                </p>
              </div>
              <div className="space-y-2">
                <Label value="Descrição" className="font-semibold" />
                <Textarea
                  placeholder="Conte brevemente o que você espera dessa captura"
                  rows={3}
                  value={formDescription}
                  onChange={(event) => setFormDescription(event.target.value)}
                />
                <p className="text-[11px] text-gray-500">
                  Use para contextualizar o objetivo do formulário e aumentar
                  conversão.
                </p>
              </div>
            </div>

            <div
              className={`${selectedFieldsPanelClass}`}
              onDragOver={handleSelectedFieldsDragOver}
              onDrop={handleSelectedFieldsDrop}
              onDragLeave={handleSelectedFieldsDragLeave}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700">
                    Campos do formulário
                  </h3>
                  <p className="text-[11px] text-gray-500">
                    Organize os campos selecionados, ajuste labels e marque os
                    obrigatórios.
                    <br />
                    <span className="text-[10px] text-gray-400">
                      Nome, telefone e campos customizados marcados como
                      obrigatórios não podem ser removidos.
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs uppercase tracking-wide text-gray-400">
                    {selectedFields.length} selecionados
                  </span>
                  <Button
                    size="xs"
                    color="gray"
                    className="lg:hidden items-center gap-2"
                    onClick={() => setDrawerOpen(true)}
                  >
                    <span>Campos</span>
                    <HiChevronDoubleRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {selectedFields.length === 0 ? (
                <p className="text-xs text-gray-500">
                  Selecione ao menos um campo para o formulário público.
                </p>
              ) : (
                <div className="space-y-3">
                  {selectedFields.map((field, idx) => {
                    const showDropIndicator =
                      dropTargetIndex === idx && isDragOverFields;
                    const highlightDropTarget =
                      dropTargetIndex === idx && dragFieldIndex !== idx;
                    return (
                      <React.Fragment key={field.id}>
                        {showDropIndicator && (
                          <div
                            className="rounded-2xl border border-dashed border-orange-300 bg-orange-50 px-3 py-1 text-xs text-slate-500 shadow-sm"
                            onDragOver={handleDropIndicatorDragOver(idx)}
                            onDrop={handleFieldDrop}
                          >
                            Arraste para cá
                          </div>
                        )}
                        <div
                          draggable
                          onDragStart={handleFieldDragStart(idx)}
                          onDragOver={handleFieldDragOver(idx)}
                          onDrop={handleFieldDrop}
                          onDragEnd={handleFieldDragEnd}
                          className={[
                            "rounded-2xl border p-2 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-300",
                            highlightDropTarget
                              ? "border-orange-400 bg-orange-50 shadow-orange-100"
                              : "border-gray-200 bg-white/80 shadow-slate-100",
                          ].join(" ")}
                        >
                          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {field.label || SOURCE_LABELS[field.source]}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                                <span className="text-orange-500 italic">
                                  {SOURCE_LABELS[field.source]}
                                </span>
                                <span>{getFieldTypeLabel(field.type)}</span>
                                {field.systemRequired && (
                                  <span className="rounded-full border border-red-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-red-500">
                                    Obrigatório
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                size="xs"
                                color="gray"
                                disabled={idx === 0}
                                onClick={() => moveField(idx, idx - 1)}
                              >
                                <HiArrowUp />
                              </Button>
                              <Button
                                size="xs"
                                color="gray"
                                disabled={idx === selectedFields.length - 1}
                                onClick={() => moveField(idx, idx + 1)}
                              >
                                <HiArrowDown />
                              </Button>
                              {field.systemRequired ? (
                                <span className="rounded-full border border-red-200 px-2 py-[2px] text-[11px] font-semibold uppercase tracking-[0.2em] text-red-500">
                                  Obrigatório
                                </span>
                              ) : (
                                <Button
                                  size="xs"
                                  color="failure"
                                  className="self-start lg:self-auto"
                                  onClick={() => removeField(idx)}
                                >
                                  <HiTrash />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="mt-3 grid gap-2">
                            <div className="space-y-1">
                              <Label value="Label do formulário" />
                              <TextInput
                                value={field.label}
                                placeholder={SOURCE_LABELS[field.source]}
                                onChange={(e) =>
                                  updateField(idx, { label: e.target.value })
                                }
                              />
                              <p className="text-[10px] text-gray-400">
                                Essa label será exibida no formulário público.
                              </p>
                            </div>
                            {!field.systemRequired && (
                              <div className="space-y-1">
                                <div className="flex items-center gap-1">
                                  <Checkbox
                                    id={`required-${field.id}`}
                                    label="Marcar como obrigatório"
                                    checked={field.required}
                                    onChange={(e) =>
                                      updateField(idx, {
                                        required: e.target.checked,
                                      })
                                    }
                                  />
                                  <span className="text-[11px]">
                                    Obrigatório
                                  </span>
                                </div>
                                <p className="text-[10px] text-gray-400">
                                  O campo será exigido antes de concluir o
                                  envio.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  {dropTargetIndex === selectedFields.length &&
                    dragFieldIndex !== null &&
                    isDragOverFields && (
                      <div
                        className="rounded-2xl border border-dashed border-orange-300 bg-orange-50 px-3 py-1 text-xs text-slate-500 shadow-sm"
                        onDragOver={handleDropIndicatorDragOver(
                          selectedFields.length
                        )}
                        onDrop={handleFieldDrop}
                      >
                        Arraste para o final
                      </div>
                    )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3">
              <Button color="gray" size="md" onClick={() => navigate(-1)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={!canSave}>
                {mutation.isLoading ? "Salvando…" : "Salvar formulário"}
              </Button>
            </div>
          </div>
        </div>
        <aside className="hidden lg:block lg:w-[400px] shrink-0">
          {renderAvailableFields()}
        </aside>
      </div>
      {drawerOpen && (
        <div className="lg:hidden">
          <div
            className="fixed inset-0 z-40 bg-black/30"
            onClick={() => setDrawerOpen(false)}
          />
          <div
            className="fixed inset-y-0 right-0 z-50 w-full max-w-sm transform overflow-y-auto bg-white p-4 shadow-2xl transition-transform duration-200"
            style={{ boxShadow: "0 40px 80px rgba(15,23,42,0.2)" }}
          >
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.35em] text-orange-500">
                Campos disponíveis
              </p>
              <Button
                size="xs"
                color="gray"
                className="flex items-center gap-1"
                onClick={() => setDrawerOpen(false)}
              >
                <HiX className="h-4 w-4" />
                Fechar
              </Button>
            </div>
            {renderAvailableFields()}
          </div>
        </div>
      )}

      <AnimatedToast
        open={Boolean(animatedToast)}
        message={animatedToast?.message}
        title={animatedToast?.title || "Sucesso"}
        onClose={() => setAnimatedToast(null)}
      />
    </div>
  );
}
